/**
 * VendorPublicPage.jsx — /v/:vendorSlug — public vendor showcase.
 *
 * One SEO-friendly URL per vendor that renders their full HustleSuite
 * story: hero + tagline, upcoming HustleEvents, active voucher
 * templates, reviews, and a contact/CTA. Fully public — no auth.
 *
 * Reads:
 *   GET /api/vendors/{vendor_slug}/public
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import {
  Calendar as CalendarIcon, MapPin, Star, Ticket, Gift,
  Facebook, Instagram, Globe, Phone, ArrowRight,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function fmtMoney(cents = 0, cur = "AUD") {
  return `$${(cents / 100).toFixed(2)} ${cur}`;
}
function fmtWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/** Star badge — supports .5 rounding for the rating pill. */
function RatingBadge({ rating, count }) {
  if (!count) return null;
  return (
    <span
      data-testid="vendor-rating-badge"
      className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 border border-amber-500/30 px-3 py-1 text-amber-500 text-sm font-black"
    >
      <Star size={13} className="fill-amber-500" />
      {rating.toFixed(1)} <span className="opacity-70 font-medium">({count})</span>
    </span>
  );
}

function EventCard({ ev, vendorSlug }) {
  const price = ev.min_price_cents
    ? fmtMoney(ev.min_price_cents, ev.currency)
    : "";
  return (
    <Link
      to={`/hustle-events/${ev.slug}`}
      data-testid={`vendor-event-${ev.id}`}
      className="group rounded-2xl overflow-hidden border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 hover:border-amber-400 transition"
    >
      <div
        className="h-40 bg-gradient-to-br from-amber-500 to-orange-600 relative"
        style={ev.cover_image ? {
          backgroundImage: `url(${ev.cover_image})`,
          backgroundSize: "cover", backgroundPosition: "center",
        } : undefined}
      >
        <span className="absolute top-3 left-3 bg-white/90 dark:bg-slate-950/80 text-amber-500 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full inline-flex items-center gap-1">
          <Ticket size={11}/> Live event
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-black text-lg group-hover:text-amber-500 transition line-clamp-2">
          {ev.title}
        </h3>
        <p className="mt-2 text-xs text-stone-500 inline-flex items-center gap-1">
          <CalendarIcon size={12}/> {fmtWhen(ev.starts_at)}
        </p>
        {ev.venue_name && (
          <p className="mt-1 text-xs text-stone-500 inline-flex items-center gap-1">
            <MapPin size={12}/> {ev.venue_name}
          </p>
        )}
        <div className="mt-3 flex items-center justify-between">
          {price && (
            <p className="text-lg font-black text-amber-500">
              from {price}
            </p>
          )}
          <span className="text-xs font-bold text-amber-500 inline-flex items-center gap-1">
            Get tickets <ArrowRight size={12}/>
          </span>
        </div>
      </div>
    </Link>
  );
}

function VoucherCard({ tpl }) {
  const savings = Math.max(0, tpl.face_value_cents - tpl.purchase_price_cents);
  const pctOff = tpl.face_value_cents > 0
    ? Math.round(savings * 100 / tpl.face_value_cents) : 0;
  return (
    <Link
      to={`/vouchers?tpl=${tpl.id}`}
      data-testid={`vendor-voucher-${tpl.id}`}
      className="group rounded-2xl overflow-hidden border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 hover:border-amber-400 transition"
    >
      <div
        className="h-36 bg-gradient-to-br from-amber-500 to-orange-600 relative"
        style={tpl.image_url ? {
          backgroundImage: `url(${tpl.image_url})`,
          backgroundSize: "cover", backgroundPosition: "center",
        } : undefined}
      >
        {pctOff > 0 && (
          <span className="absolute top-3 right-3 bg-amber-400 text-slate-950 text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
            {pctOff}% OFF
          </span>
        )}
        {tpl.is_gift_ready && (
          <span className="absolute top-3 left-3 bg-white/90 dark:bg-slate-950/80 text-amber-500 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full inline-flex items-center gap-1">
            <Gift size={11}/> Gift-ready
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-black text-base group-hover:text-amber-500 transition line-clamp-2">
          {tpl.item_description}
        </h3>
        <div className="mt-2 flex items-baseline gap-2">
          <p className="text-lg font-black text-amber-500">
            {fmtMoney(tpl.purchase_price_cents, tpl.currency)}
          </p>
          {savings > 0 && (
            <p className="text-xs text-stone-500 line-through">
              {fmtMoney(tpl.face_value_cents, tpl.currency)}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

function ReviewCard({ rev }) {
  return (
    <li
      data-testid={`vendor-review-${rev.id}`}
      className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-5"
    >
      <div className="flex items-center gap-2">
        <span className="text-amber-500 font-black text-lg">
          {"★".repeat(rev.rating)}
          <span className="text-stone-300 dark:text-stone-700">
            {"★".repeat(Math.max(0, 5 - rev.rating))}
          </span>
        </span>
        <span className="text-xs text-stone-500 ml-auto">
          {rev.created_at?.slice(0, 10)}
        </span>
      </div>
      {rev.title && (
        <p className="mt-2 font-bold">{rev.title}</p>
      )}
      {rev.body && (
        <p className="mt-1 text-sm text-stone-600 dark:text-stone-400 line-clamp-4">
          {rev.body}
        </p>
      )}
      <p className="mt-3 text-xs uppercase tracking-widest text-stone-400 font-bold">
        — {rev.customer_name || "Guest"}
      </p>
      {(rev.replies || []).slice(0, 1).map((rp, idx) => (
        <div
          key={idx}
          className="mt-3 rounded-xl bg-amber-500/5 border border-amber-500/20 p-3 text-sm"
        >
          <p className="text-[10px] uppercase tracking-widest text-amber-500 font-black mb-1">
            Owner reply
          </p>
          <p className="text-stone-600 dark:text-stone-300">{rp.body}</p>
        </div>
      ))}
    </li>
  );
}

export default function VendorPublicPage() {
  const { vendorSlug } = useParams();
  const [data, setData]   = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setData(null); setError("");
        const { data: d } = await axios.get(
          `${API}/vendors/${encodeURIComponent(vendorSlug)}/public`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(
          e?.response?.data?.detail || e.message || "Failed to load vendor");
      }
    })();
    return () => { cancelled = true; };
  }, [vendorSlug]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0908] text-stone-900 dark:text-stone-100 flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="vendor-error">
          <h1 className="text-2xl font-black">Vendor not found</h1>
          <p className="mt-3 text-stone-500">{error}</p>
          <Link
            to="/vouchers"
            className="inline-block mt-6 text-amber-500 font-bold hover:underline"
          >
            Browse HustleSuite deals →
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        data-testid="vendor-loading"
        className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center text-stone-400"
      >
        Loading vendor…
      </div>
    );
  }

  const { vendor, events, vouchers, reviews } = data;
  const socials = vendor.socials || {};
  const hasEvents   = (events || []).length > 0;
  const hasVouchers = (vouchers || []).length > 0;
  const hasReviews  = (reviews || []).length > 0;

  return (
    <div
      data-testid="vendor-public-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: vendor.name || "HustleSuite Vendor", to: `/v/${vendorSlug}` }}
        nav={[
          hasEvents   && { to: "#events",   label: "Events" },
          hasVouchers && { to: "#vouchers", label: "Vouchers" },
          hasReviews  && { to: "#reviews",  label: "Reviews" },
          { to: "#contact", label: "Contact" },
        ].filter(Boolean)}
        showBookCta={false}
        showCart={false}
      />

      {/* Hero */}
      <section
        data-testid="vendor-hero"
        className="relative overflow-hidden border-b border-stone-200 dark:border-stone-800"
        style={vendor.hero_url ? {
          backgroundImage: `linear-gradient(135deg, rgba(10,9,8,.6), rgba(10,9,8,.75)), url(${vendor.hero_url})`,
          backgroundSize: "cover", backgroundPosition: "center",
        } : { background: "linear-gradient(135deg,#0a0908,#1c1917)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-white relative">
          <div className="flex items-center gap-4">
            {vendor.logo_url && (
              <img
                src={vendor.logo_url} alt={vendor.name}
                className="h-20 w-20 rounded-2xl object-cover bg-white/10 border border-white/20"
                data-testid="vendor-logo"
              />
            )}
            <div>
              <p className="text-amber-400 text-[11px] font-black uppercase tracking-[0.32em]">
                HustleSuite Vendor
              </p>
              <h1
                data-testid="vendor-name"
                className="mt-1 text-3xl md:text-5xl font-black leading-tight text-white"
              >
                {vendor.name}
              </h1>
            </div>
          </div>
          {vendor.tagline && (
            <p
              className="mt-4 text-lg md:text-xl text-white/80 max-w-2xl"
              data-testid="vendor-tagline"
            >
              {vendor.tagline}
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <RatingBadge
              rating={vendor.rating || 0}
              count={vendor.review_count || 0}
            />
            {vendor.address && (
              <span className="inline-flex items-center gap-1 text-sm text-white/80">
                <MapPin size={14}/> {vendor.address}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-16">
        {/* Events */}
        {hasEvents && (
          <section id="events" data-testid="vendor-events-section">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="text-2xl font-black">Upcoming events</h2>
              <Link
                to="/hustle-events"
                className="text-sm font-bold text-amber-500 inline-flex items-center gap-1 hover:underline"
              >
                All events <ArrowRight size={14}/>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((ev) => (
                <EventCard key={ev.id} ev={ev} vendorSlug={vendorSlug}/>
              ))}
            </div>
          </section>
        )}

        {/* Vouchers */}
        {hasVouchers && (
          <section id="vouchers" data-testid="vendor-vouchers-section">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="text-2xl font-black">Vouchers & gifts</h2>
              <Link
                to="/vouchers"
                className="text-sm font-bold text-amber-500 inline-flex items-center gap-1 hover:underline"
              >
                All deals <ArrowRight size={14}/>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vouchers.map((tpl) => (
                <VoucherCard key={tpl.id} tpl={tpl}/>
              ))}
            </div>
          </section>
        )}

        {/* About / description */}
        {vendor.description && (
          <section id="about" data-testid="vendor-about-section">
            <h2 className="text-2xl font-black mb-4">About {vendor.name}</h2>
            <p className="text-base leading-relaxed text-stone-600 dark:text-stone-300 whitespace-pre-line max-w-3xl">
              {vendor.description}
            </p>
          </section>
        )}

        {/* Reviews */}
        {hasReviews && (
          <section id="reviews" data-testid="vendor-reviews-section">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="text-2xl font-black">
                Reviews
                <span className="ml-2 text-base text-stone-500">
                  {vendor.rating?.toFixed(1)} ★ · {vendor.review_count} verified
                </span>
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {reviews.map((rev) => (
                <ReviewCard key={rev.id} rev={rev}/>
              ))}
            </ul>
          </section>
        )}

        {/* Empty state — vendor with no bookings yet */}
        {!hasEvents && !hasVouchers && !hasReviews && (
          <div
            data-testid="vendor-empty"
            className="rounded-2xl border border-dashed border-stone-300 dark:border-stone-700 p-10 text-center"
          >
            <Ticket size={28} className="mx-auto text-amber-500"/>
            <p className="mt-4 font-semibold">
              {vendor.name} is joining HustleSuite — deals and events are landing soon.
            </p>
          </div>
        )}

        {/* Contact / CTA */}
        <section
          id="contact"
          data-testid="vendor-contact-section"
          className="rounded-3xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-8 md:p-10"
        >
          <div className="md:flex md:items-center md:justify-between gap-6">
            <div>
              <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
                Get in touch
              </p>
              <h2 className="mt-2 text-2xl font-black">Book, gift or follow {vendor.name}</h2>
              <p className="mt-2 text-sm text-stone-500 max-w-md">
                HustleSuite is the Merchant of Record — every dollar is
                traceable from buyer to vendor with OFT-grade transparency.
              </p>
            </div>
            <div className="mt-5 md:mt-0 flex flex-wrap items-center gap-3">
              {hasEvents && (
                <Button
                  asChild
                  className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
                  data-testid="vendor-cta-events"
                >
                  <a href="#events">See upcoming events</a>
                </Button>
              )}
              {hasVouchers && (
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-amber-400 text-amber-500 hover:bg-amber-500/10 font-black"
                  data-testid="vendor-cta-vouchers"
                >
                  <a href="#vouchers">Send a gift</a>
                </Button>
              )}
            </div>
          </div>

          {(vendor.phone || socials.facebook || socials.instagram || vendor.website) && (
            <div className="mt-8 pt-6 border-t border-stone-200 dark:border-stone-800 flex flex-wrap items-center gap-5 text-sm text-stone-500">
              {vendor.phone && (
                <a
                  href={`tel:${vendor.phone}`}
                  data-testid="vendor-phone"
                  className="inline-flex items-center gap-1 hover:text-amber-500"
                >
                  <Phone size={14}/> {vendor.phone}
                </a>
              )}
              {vendor.website && (
                <a
                  href={vendor.website} target="_blank" rel="noreferrer"
                  data-testid="vendor-website"
                  className="inline-flex items-center gap-1 hover:text-amber-500"
                >
                  <Globe size={14}/> Website
                </a>
              )}
              {socials.facebook && (
                <a
                  href={socials.facebook} target="_blank" rel="noreferrer"
                  data-testid="vendor-facebook"
                  className="inline-flex items-center gap-1 hover:text-amber-500"
                >
                  <Facebook size={14}/> Facebook
                </a>
              )}
              {socials.instagram && (
                <a
                  href={socials.instagram} target="_blank" rel="noreferrer"
                  data-testid="vendor-instagram"
                  className="inline-flex items-center gap-1 hover:text-amber-500"
                >
                  <Instagram size={14}/> Instagram
                </a>
              )}
            </div>
          )}
        </section>
      </div>

      <SuiteFooter />
    </div>
  );
}
